import React, { useEffect, useState, useRef } from "react";
import '../Treatment_Authorization/requeststyle.css';
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { Endpoints } from "../../Global/Endpoints";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import ErrorModal from "../Security/ErrorModal";
import CustomDropdown from "../../CustomControls/dropdown";
import { ToastContainer } from "react-toastify";
import { renderAsync } from 'docx-preview';
import * as pdfjsLib from "pdfjs-dist";
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const RequestAuthorization = () => {
  const userInfo = useSelector((state) => state.userData);
  const token = localStorage.getItem('AccessToken');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [requestedServices, setRequestedServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [practiceResults, setPracticeResults] = useState([]);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showNurseModal, setShowNurseModal] = useState(false);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [fax, setFax] = useState("");
  const [postalZip, setPostalZip] = useState("");
  const [country, setCountry] = useState("");
  const [countryOptions, setCountryOptions] = useState([]);
  const practiceResultsRef = useRef(null); // ref for dropdown list
  const location = useLocation();
  const ucReqAuth = location.state?.ucReqAuth || "N/A";
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isFileError, setIsFileError] = useState(false);
  const [masterPracticeData, setMasterPracticeData] = useState([]); // Stores the API response from length === 3
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [providerName,setProviderName]= useState("");
  const [providerResults, setProviderResults] = useState([]);
  const providerResultsRef = useRef(null);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [isDocsUploading, setIsDocUploading] = useState(false);

  const getPageCount = (file) => {
      return new Promise(async (resolve, reject) => {
          const bytes = await file.arrayBuffer()
          
          const loadingTask = pdfjsLib.getDocument({
              data: bytes,
              disableWorker: true});
            try {
              const pdf = await loadingTask.promise;
              const pages = pdf.numPages;
              resolve(pages);
            } catch (error) {
              reject(error);
          }
      });
  };

  useEffect(() => {
    const fetchRequestedServices = async () => {
      setIsItemsLoading(true);
      try {
        const params = {
          MemberID: userInfo.userData.MemberID,
          Contract: userInfo.userData.ContractIdentified || "",
          EmailID: userInfo.userData.EmailID,
          DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
        };

        const response = await axios.post(
          Endpoints.GetProcedurePackageList,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Connection": "close",
              "Accept": "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setRequestedServices(response.data);
      } catch (error) {
        console.error("Error fetching requested services:", error);
      } finally {
        setIsItemsLoading(false);
      }
    };

    const fetchCountries = async () => {
      setIsItemsLoading(true);
      try {
        const params = {
          EmailID: userInfo.userData.EmailID,
          DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
        };

        const response = await axios.post(
          Endpoints.LoadCountry,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data) {
          const options = response.data
            .map(c => c.ShortName || "")
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          setCountryOptions(options);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setIsItemsLoading(false);
      }
    };

    fetchRequestedServices();
    fetchCountries();
  }, []);

  // Outside click listener to clear practice dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        practiceResultsRef.current &&
        !practiceResultsRef.current.contains(event.target)
      ) {
        setPracticeResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleFileSelection = (event) => {
    const validExtensions = ["pdf", "tiff", "jpeg", "jpg", "bmp", "png"];
    const files = Array.from(event.target.files);
    let oversizedFileName = "";
    let duplicateFound = false;

    // Check for duplicates with already selected files
    for (const file of files) {
      const isDuplicate = selectedFiles.some(
        f => f.name === file.name && f.size === file.size
      );
      if (isDuplicate) {
        duplicateFound = true;
        break;
      }
    }

    // Check for duplicates within the new files themselves
    const fileSignatures = new Set();
    for (const file of files) {
      const signature = `${file.name}_${file.size}`;
      if (fileSignatures.has(signature)) {
        duplicateFound = true;
        break;
      }
      fileSignatures.add(signature);
    }

    if (duplicateFound) {
      setModalMessage("Duplicate file(s) detected. Please select unique files only.");
      setIsFileError(true);
      setShowModal(true);
      if (event.target) event.target.value = null;
      return;
    }

    if((files.length + selectedFiles.length)>4) {
      setModalMessage("Maximum 4 files can be selected");
      setIsFileError(true);
      setShowModal(true);
      if (event.target) event.target.value = null;
      return;
    }

    const validFiles = files.filter(file => {
      const fileExtension = file.name.split('.').pop().toLowerCase(); // Extract file extension
      if (file.size > 5 * 1024 * 1024) {
        oversizedFileName = file.name;
        return false;
      }
      return validExtensions.includes(fileExtension);
    });
    if (validFiles.length > 0) {
      setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
    }

    if (validFiles.length !== files.length) {
    // Check if there were invalid files
      setModalMessage("Invalid file type. Only PDF, TIFF, JPEG, BMP, or PNG allowed.");
      setIsFileError(true); // Reset error state
      setShowModal(true);
    }
    if (oversizedFileName) {
      setModalMessage(`The ${oversizedFileName} is too large (over 5MB). Please upload a smaller file.`);
      setIsFileError(true);
      setShowModal(true);
      return;
    }
    // Clear the file input value to allow re-uploading the same file
    if (event.target) {
      event.target.value = null;
    }

  };

const handleViewFile = async (file) => {
  const fileType = file.type;

  if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      const fileArrayBuffer = await file.arrayBuffer();
      
      // Open a new window
      const newWindow = window.open("", "_blank");
      if (newWindow) {
          // Write basic HTML structure to new window
          newWindow.document.write(`
              <html>
                  <head>
                      <title>Document Preview</title>
                      <style>body { font-family: Arial, sans-serif; margin: 20px; }</style>
                  </head>
                  <body>
                      <div id="docx-preview"></div>
                  </body>
              </html>
          `);
          newWindow.document.close();

          // Render the document into the new window
          renderAsync(fileArrayBuffer, newWindow.document.getElementById("docx-preview"));
      }
  } else {
      // For other types, open directly
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
  }
};
const handleFileDelete = (index) => {
  setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
};

  const searchPracticeName = async (practiceName) => {
    debugger
    setPracticeName(practiceName); // Clear previous results

    try {
      const params = {
        DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
        EmailID: userInfo.userData.EmailID,
        GroupName: practiceName,
        Contract: userInfo.userData.ContractIdentified,
        Country: country || "",
      };

      const response = await axios.post(
        Endpoints.GetGroupDetails,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Connection": "close",
            "Accept": "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const responseData = Array.isArray(response.data) ? response.data : [];
      setMasterPracticeData(responseData);
      setPracticeResults(responseData);
    } catch (error) {
      console.error("Error searching practice name:", error);
      setPracticeResults([]);
    }
  };

  const handlePracticeSelect = (practice) => {
    setSelectedPractice(practice);
    fetchProviders(practice.GroupID);
    setPracticeResults([]);
  };

  const fetchProviders = async (groupId) => {
    try {
      const params = {
        DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
        GroupID: groupId,
        EmailID: userInfo.userData.EmailID,
        Contract: userInfo.userData.ContractIdentified,
      };
      const response = await axios.post(
        Endpoints.GetProviderDetails,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Connection": "close",
            "Accept": "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProviders(response.data);
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  const handleNextClick = async () => {
    debugger;

    if (!selectedService || !selectedDate || (!selectedPractice && !practiceName) || (!selectedProvider && !providerName) || (ucReqAuth === "Yes" && (!address || !phone || !fax || !postalZip || !country))) {
      setModalMessage("Please complete all required fields.");
      setShowModal(true);
      return;
    }

    // Validation for dynamic questions
  if (ucReqAuth === "Yes" && dynamicQuestions.length > 0) {
    const unanswered = dynamicQuestions.find(q => !dynamicAnswers[q.key]);
    if (unanswered) {
      setModalMessage("Please answer all mandatory questions.");
      setShowModal(true);
      return;
    }
    // Mandatory upload file validation
    if (!selectedFiles || selectedFiles.length === 0) {
      setModalMessage("Please upload at least one medical record document.");
      setShowModal(true);
      return;
    }
  }

    const sendToWinSuccess = await handleSendToWin();
    if (!sendToWinSuccess) {
      return;
    }
    // Map dynamic answers to API fields
    const questionFieldMap = {
      // IVF
      frozenEmbryos: "FrozenEmbryos",
      // PGT
      medicalCondition: "SeriousCondition",
      singleGene: "GeneOrChromosome",
      sameGene: "SameGeneCarrier",
      childWithCondition: "ChildWithCarrierCondition",
      geneEvenIfOtherParent: "SingleParentGeneCarrier",
      xLinked: "XLinkedCondition",
      // IUI
      hypogonadotropic: "HypogonadotropicDisorder",
      oralMedication: "OralMedAttempts",
      // Egg/Sperm Freezing
      medicalProcedure: "FertilityRiskReason"
    };

    // Build dynamic question fields for API
    const dynamicFields = {};
    Object.entries(questionFieldMap).forEach(([dynKey, apiKey]) => {
      dynamicFields[apiKey] = dynamicAnswers[dynKey] || "";
    });

    if (ucReqAuth === "Yes") {
      const params = {
        memberId: userInfo.userData.MemberID,
        contract: userInfo.userData.ContractIdentified,
        AuthNO: selectedService?.AuthNO || "",
        groupname: selectedPractice?.GroupName || practiceName || "",
        providername: selectedProvider?.ProName || providerName || "",
        procedureCode: selectedService?.CPTCode || "",
        reqStartDate: selectedDate ? selectedDate.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "",
        GroupID: selectedPractice?.GroupID || "",
        ProId: selectedProvider?.ProId || "",
        FrozenEmbryos: dynamicFields.FrozenEmbryos,
        SeriousCondition: dynamicFields.SeriousCondition,
        GeneOrChromosome: dynamicFields.GeneOrChromosome,
        SameGeneCarrier: dynamicFields.SameGeneCarrier,
        ChildWithCarrierCondition: dynamicFields.ChildWithCarrierCondition,
        SingleParentGeneCarrier: dynamicFields.SingleParentGeneCarrier,
        XLinkedCondition: dynamicFields.XLinkedCondition,
        HypogonadotropicDisorder: dynamicFields.HypogonadotropicDisorder,
        OralMedAttempts: dynamicFields.OralMedAttempts,
        FertilityRiskReason: dynamicFields.FertilityRiskReason,
        Address: address || "",
        ContactPhone: phone || "",
        Fax: fax || "",
        country: country || "",
        EmailID: userInfo.userData.EmailID || "",
        DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal", // or use actual device type if available
        PostalZipcode: postalZip || ""
      };
      setLoading(true);
      debugger
      console.log("Params for UCMemberReqAuth:", params);
      try {
        const response = await axios.post(
          Endpoints.UCMemberReqAuth,
          { _Params: JSON.stringify(params) },
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              "Accept": "*/*",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Response from UCMemberReqAuth:", response.data);
        if (response.data && response.data.result === 1) {

          setModalMessage("Details saved successfully");
          setShowNurseModal(true);
        } else {
          setModalMessage("Failed to submit the request. Please try again.");
          setShowModal(true);
        }
      } catch (error) {
        console.error("Error submitting the authorization request:", error);
        setModalMessage("An error occurred while submitting the request. Please try again.");
        setShowModal(true);
      }
      finally {
        setLoading(false);  }
      return;
    }

    // Default: MemberReqAuth
    const params = {
      reqStartDate: selectedDate.toLocaleDateString("en-US"),
      ProId: selectedProvider?.ProId || "",
      procedureCode: selectedService.CPTCode,
      EmailID: userInfo.userData.EmailID,
      DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
      contract: userInfo.userData.ContractIdentified,
      memberId: userInfo.userData.MemberID,
      GroupID: selectedPractice?.GroupID || "",
      PostalZipcode: postalZip || "",
      ...(ucReqAuth === "Yes" && {
        address,
        phone,
        fax,
        country
      })
    };
    setLoading(true);
    try {
      const response = await axios.post(
        Endpoints.MemberReqAuth,
        { _Params: JSON.stringify(params) },
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "*/*",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.result === 1) {
        setModalMessage(response.data.resultText);
        setShowNurseModal(true);
      } else {
        setModalMessage("Failed to submit the request. Please try again.");
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error submitting the authorization request:", error);
      setModalMessage("An error occurred while submitting the request. Please try again.");
      setShowModal(true);
    }
    finally {
      setLoading(false);
    }
  };

  const handleServiceChange = (service) => {
    setSelectedService(service);
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider); // This is the key state change
    setProviderName(provider.ProName); // Keep the input text in sync
    setProviderResults([]); // Clear the dropdown results
    
    // If UC Auth is "Yes", auto-fill the contact info from the provider object
    if (ucReqAuth === "Yes") {
      setAddress(provider.Address || "");
      setPhone(provider.Phone || "");
      setFax(provider.Fax || "");
    }
  };

  const handleBackClick = () => navigate(-1);
  const handleModalClose = () => setShowModal(false);
  const handleNurseModalClose = () => {
    setShowNurseModal(false);
    navigate(-1);
  };

  // State for dynamic questions and upload
  const [dynamicAnswers, setDynamicAnswers] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Helper to clear dynamic answers and file
  const handleCancelDynamic = () => {
    setDynamicAnswers({});
    setUploadedFiles([]);
  };

  // Dynamic question sets by service
  const dynamicQuestions = (() => {
    if (!selectedService?.Package) return [];
    switch (selectedService.Package) {
      case "In Vitro Fertilization (IVF)":
        return [
          { key: "frozenEmbryos", text: "Do you have any frozen embryos available from a previous cycle?" }
        ];
      case "Preimplantation Genetic Testing of Embryos (PGT)":
        return [
          { key: "medicalCondition", text: "Do you have a medical condition that could cause serious health problems?" },
          { key: "singleGene", text: "Is the condition caused by a single gene or change in the structure of chromosomes?" },
          { key: "sameGene", text: "Do you and your partner carry the same gene that could cause a health problem in a baby?" },
          { key: "childWithCondition", text: "Have you or your partner had a child with a health condition that you or your partner carry?" },
          { key: "geneEvenIfOtherParent", text: "Do you or your partner carry a gene that could cause a health condition in a baby even if the other parent does not?" },
          { key: "xLinked", text: "Do you or your partner have a known X-linked recessive condition (condition that affects boys)?" }
        ];
      case "Intrauterine Insemination (IUI) with Injectable Medication (ex. Follistim, Menopur)":
        return [
          { key: "hypogonadotropic", text: "Do you have a diagnosis of hypogonadotropic anovulatory disorder or hypopituitarism (which means your pituitary gland is not working)?" },
          { key: "oralMedication", text: "Have you attempted to conceive with oral medication (such as Clomid or letrozole (Femara)) at least 3 times?" }
        ];
      case "Egg Freezing":
      case "Sperm Freezing":
        return [
          { key: "medicalProcedure", text: "Are you facing a medical procedure or medication that may decrease your ability to conceive in the future and that is the reason for the egg or sperm freezing?" }
        ];
      default:
        return [];
    }
  })();

  // Upload handler for WIN API
  const handleSendToWin = async () => {
    if (!selectedFiles) {
        setModalMessage("Please select a document.");
        setShowModal(true);
        return false;
    }
    
        try {
            let fileID = 1; // Initialize FileID to start from 1
            const payloads = []; // Array to hold all file payloads
    
            for (const file of selectedFiles) {
                let noOfPages = 1;
    
                // Determine the number of pages based on file type
                try {
                  const fileType = file.type;
  
                  if (fileType === "application/pdf") {
                      noOfPages = await getPageCount(file);
                  } else if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
                      const arrayBuffer = await file.arrayBuffer();
                      const content = await mammoth.extractRawText({ arrayBuffer });
                      const wordCount = content.value.split(/\s+/).length;
                      noOfPages = Math.ceil(wordCount / 500);
                  } else if (fileType === "text/plain") {
                      const textContent = await file.text();
                      const lineCount = textContent.split(/\r?\n/).length;
                      noOfPages = Math.ceil(lineCount / 50);
                  }
                } catch (pageError) {
                    console.warn(`Failed to determine page count for ${file.name}:`, pageError);
                }
    
                const fileToBase64 = (file) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result.split(",")[1]);
                        reader.onerror = (error) => reject(error);
                        reader.readAsDataURL(file);
                    });
    
                const base64Data = await fileToBase64(file);
    
                // Create payload for each file
                const filePayload = {
                    FileID: fileID.toString(), // Use the incrementing FileID
                    FilePath: "",
                    FileName: file.name,
                    NoOFPages: noOfPages.toString(),
                    DocTypeDesc: "UCHealth Medical Report",
                    inputBytes: base64Data,
                    fileSize: file.size.toString(),
                    memberId: userInfo.userData.MemberID,
                    contract: userInfo.userData.ContractIdentified,
                    contractCode: "",
                    docTypeDescription: "UCHealth Medical Report",
                    DeviceType:isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
                    EmailID: userInfo.userData.EmailID
                };
    
                payloads.push(filePayload);
                fileID++; // Increment FileID for the next file
            }
            const totalFiles = payloads.length;
            // debugger
            setLoading(true);
            setIsDocUploading(true);
            // Send all files in a single request
            const response = await axios.post(
                Endpoints.UploadDocument,
                JSON.stringify(payloads),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            // debugger
            console.log("Upload response:", response.data);

    // Build DocumentTypes and Documents arrays  
    const sendToWinPayload = {
      _Params: JSON.stringify({
        MemberID: userInfo.userData.MemberID ?? "",
        Contract: userInfo.userData.ContractIdentified ?? "",
        DoctypeDesc: "UCHealth Medical Report",
        DocumentTypes: "",
        TotalFiles: totalFiles,
        Documents: response.data.map((file, index) => ({
                      NoOFPages: payloads[index].NoOFPages,
                      FilePath: file.FilePath,
                      FileID: payloads[index].FileID,
                      FileName: payloads[index].FileName,
                      DocTypeDesc: "UCHealth Medical Report",
                    })),
        ReimbursementDetails: [],
        TotalAmount: 0,
        Speciality: "",
        PhysicianName: "",
        PhysicianTelNo: "",
        PhysicianTaxNo: "",
        ServiceType: "",
        AuthNo: "",
        Email: userInfo.userData.EmailID || null,
        UserName: "MAPP",
        ProviderID: "",
        GroupID: "",
        Activity: "",
        DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal"
      }),
   };      

      const res = await axios.post(
        Endpoints.SendToWin,
        sendToWinPayload,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data?.Result === undefined || Number(res.data?.Result) === 2) {
        setUploadedFiles([]);
        return true;
      }

      setModalMessage(res.data?.ResultText || "Failed to upload documents. Please try again.");
      setShowModal(true);
      return false;
    }catch (error) {
      setModalMessage("An error occurred while uploading documents. Please try again.");
      setShowModal(true);
      return false;
    } finally {
      setLoading(false);
      setIsDocUploading(false);
    }
    };

  if (loading) {
    return (
        <div className="loading-container">
            <div className="loading mb-2"></div>
                <h2 className="loading-text">
                {!isDocsUploading ? (
                    t("loading")
                ) : (
                    <>
                    Your documents are being securely uploaded.
                    <br />
                    Please wait for a moment.
                    </>
                )}
                </h2>            
            </div>
        );
  }

  const onBrowse = () => {
    const input = document.getElementById('fileInput');
    if (input) {
        input.value = null; // Always reset so change event fires even for same file
        input.click();
    }
};

  return (
    <>
      <ToastContainer />
        {isHighmarkPortal ? 
            <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
            <button className="win-back-btn" onClick={handleBackClick}>
                <span>
                <i className="bi bi-chevron-left"></i>
                </span>
            </button>       
            <h2 className="page-heading">{t('requestAuthorizationHeading')}</h2>
            </div>:
            <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
            <button className="win-back-btn" onClick={handleBackClick}>
                <span>
                <i className="bi bi-chevron-left"></i>    
                {t('Back')}        
                </span>
            </button>       
            <h2 className="page-heading">{t('requestAuthorizationHeading')}</h2>
            </div>
        }

      <div className="col-md-6">
        <p className="instructions">{t('authorizationRequestInstructions')}</p>
        <form className="form mt-5">
          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">{t('requestedService')}</label>
            <div className="col-sm-8 custom-dropdown-forced-380px-in-tab custom-dropdown-100pc-only-mobile">
              <CustomDropdown
                options={requestedServices.map(s => s.Package)}
                selectedOption={selectedService?.Package || null}
                onChange={(val) => {
                  const service = requestedServices.find(s => s.Package === val);
                  handleServiceChange(service);
                }}
                placeholder="Select a service"
                searchable
                isLoading={loading}
              />
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">{t('date')}</label>
            <div className="col-sm-8">
              <DatePicker
                selected={selectedDate}
                onChange={setSelectedDate}
                dateFormat="MM/dd/yyyy"
                className="form-control win-form-control calendar-control mobile-view"
                placeholderText="MM/DD/YYYY"
                showYearDropdown
                scrollableYearDropdown
                onKeyDown={(e) => e.preventDefault()}
              />
            </div>
          </div>
          <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label">Country</label>
              <div className="col-sm-8">
                <CustomDropdown
                  options={countryOptions}
                  selectedOption={country || ""}
                  onChange={(val) => setCountry(val)}
                  placeholder="Select a country"
                  searchable={true}
                  isLoading={isItemsLoading}
                />
              </div>
          </div>
          <div className="mb-4 row">
            <label className="col-sm-4 mt-3 model-form-label">{t('practice_name')}</label>
            <div className="col-sm-8">
              <div className="d-flex">
                <input
                  type="text"
                  value={selectedPractice ? selectedPractice.GroupName : practiceName}
                  onChange={async (e) => {
                  const value = e.target.value;
                  // Check if country is selected before allowing practice name search
                  if (!country) {
                    setModalMessage("Please select a country before entering practice name.");
                    setShowModal(true);
                    return;
                  }
                  setPracticeName(value);
                  setSelectedPractice(null);
                  if (value.trim().length == 3) {
                    await searchPracticeName(value);
                  } else if (value.trim().length > 3) {
                    const filtered = masterPracticeData.filter(item => 
                      item.GroupName.toLowerCase().includes(value.toLowerCase())
                    );
                    setPracticeResults(filtered);
                  } else {
                    setPracticeResults([]);
                    setMasterPracticeData([]);
                  }
                }}
                  className="form-control win-form-control mobile-view"
                  placeholder="Enter practice name"
                />
              </div>

              <div ref={practiceResultsRef}>
                {practiceResults && practiceResults.length > 0 && (
                  <ul className="search-results">
                    {practiceResults.map((practice) => (
                      <li key={practice.GroupID} onClick={() => handlePracticeSelect(practice)} className="search-result-item">
                        {practice.GroupName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div><small className="helper-text">{t('viewMatchingPracticeInstruction')}</small></div>
            </div>
          </div>

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">{t('provider_name')}</label>
            <div className="col-sm-8">
              <div className="d-flex">
                <input
                  type="text"
                  value={selectedProvider ? selectedProvider.ProName : providerName}
                  onChange={async (e) => {
                    const value = e.target.value;
                    setProviderName(value);
                    if (selectedProvider) setSelectedProvider(null);
                    // Show dropdown only if 3+ chars
                    if (value.trim().length >= 3) {
                      const filtered = providers.filter(p => 
                        p.ProName.toLowerCase().includes(value.toLowerCase())
                      );
                      setProviderResults(filtered);
                    } else {
                      setProviderResults([]);
                    }
                  }}
                  className="form-control win-form-control mobile-view"
                  placeholder="Enter provider name"
                />
              </div>
              <div ref={providerResultsRef}>
                {providerResults && providerResults.length > 0 && (
                  <ul className="search-results">
                    {providerResults.map((provider) => (
                      <li
                        key={provider.ProId}
                        onClick={() => {
                         handleProviderSelect(provider)
                        }}
                        className="search-result-item"
                      >
                        {provider.ProName}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <small className="helper-text">{t('viewMatchingProviderInstruction')}</small>
              </div>
            </div>
          </div>

          {ucReqAuth === "Yes" && (selectedProvider || providerName) && (
            <>
              <div className="mb-4 row align-items-center">
                <label className="col-sm-4 model-form-label">Address</label>
                <div className="col-sm-8">
                  <input type="text" className="form-control win-form-control mobile-view" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter address" readOnly={!!selectedProvider && !providerName}/>
                </div>
              </div>
              <div className="mb-4 row align-items-center">
                <label className="col-sm-4 model-form-label">Phone</label>
                <div className="col-sm-8">
                  <input type="text" className="form-control win-form-control mobile-view" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Enter phone number" readOnly={!!selectedProvider && !providerName}/>
                </div>
              </div>
              <div className="mb-4 row align-items-center">
                <label className="col-sm-4 model-form-label">Fax #</label>
                <div className="col-sm-8">
                  <input type="text" className="form-control win-form-control mobile-view" value={fax} onChange={e => setFax(e.target.value)} placeholder="Enter fax number" readOnly={!!selectedProvider && !providerName}/>
                </div>
              </div>
           
            </>
          )}

          <div className="mb-4 row align-items-center">
            <label className="col-sm-4 model-form-label">Postal Zipcode</label>
            <div className="col-sm-8">
              <input type="text" className="form-control win-form-control mobile-view" value={postalZip} onChange={e => setPostalZip(e.target.value)} placeholder="Enter postal/zipcode" />
            </div>
          </div>
        </form>

        

        

        <ErrorModal show={showModal} message={modalMessage} onClose={handleModalClose} closeButton={true} />
        <ErrorModal show={showNurseModal} message={modalMessage} onClose={handleNurseModalClose} closeButton={true} />
      </div>

      

      <div className="col-md-6">
            {/* Dynamic questions and upload section */}
        {ucReqAuth === "Yes" &&  dynamicQuestions.length > 0 && (
          <div className="dynamic-questions-section">
   
            {dynamicQuestions.map(q => (
              <div className="mb-3" key={q.key}>
                <label className="form-label fw-bold">{q.text}</label>
                <div>
                  <label className="me-3">
                    <input
                      type="radio"
                      name={q.key}
                      value="Yes"
                      checked={dynamicAnswers[q.key] === "Yes"}
                      onChange={() => setDynamicAnswers(a => ({ ...a, [q.key]: "Yes" }))
                      }
                      required
                    /> Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={q.key}
                      value="No"
                      checked={dynamicAnswers[q.key] === "No"}
                      onChange={() => setDynamicAnswers(a => ({ ...a, [q.key]: "No" }))
                      }
                      required
                    /> No
                  </label>
                </div>
              </div>
            ))}


            <div className="mb-4 row align-items-center">
              <label className="col-sm-4 model-form-label">Please upload your medical records for review of this request</label>
              <div className="col-md-8">
                                 
                  <div class="custom-file-upload-380px">
                        <input
                            type="file"
                            id="fileInput"
                            class="file-input"
                            onChange={handleFileSelection}
                            multiple
                            ref={fileInputRef}
                            accept=".pdf, .tiff, .jpeg, .jpg, .bmp, .png"
                        />
                        <div class="input-display">
                            <input type="text" class="file-text" placeholder={t("choosefileplaceholder")} readonly />
                            <button type="button" class="custom-button"  onClick={onBrowse}>{t("browse")}</button>
                        </div>
                    </div>
                    
                    
                    {/* <button
                      className="btn btn-primary mt-2"
                      type="button"
                      onClick={handleUploadDocuments}
                    >
                      Upload
                    </button> */}
             
              </div>
            </div>
            <div className="mb-4 row align-items-center">
              <div className="mt-2">
                      <table className="table">
                        <thead className="winpay-table-header">
                            <tr className="winpay-table-header">
                                <th width="80%">{t("filenamelable")}</th>
                                <th width="140" className="text-center">{t("btnview")}</th>
                                <th width="140" className="text-center">{t("btndelete")}</th>
                            </tr>
                        </thead>
                        <tbody>
                        {selectedFiles.map((file, index) => (
                                    <tr className="winpay-table-row" key={index}>
                                        <td>{file.name}</td>
                                        <td className="text-center">
                                            <button
                                                className="winpay-table-view-icon mx-auto"
                                                onClick={() => handleViewFile(file)}
                                            >
                                            </button>
                                        </td>
                                        <td className="text-center">
                                            <button
                                                className="winpay-table-trash-icon mx-auto"
                                                onClick={() => handleFileDelete(index)}
                                            >
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {selectedFiles.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="text-center">
                                        {t('noFilesUploaded')}
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                  </div>
            </div>
          </div>
        )}
      </div>

      <div className="col-md-6">
        <div className="mb-4 row align-items-center">
          <label className="col-sm-4 model-form-label"></label>
          <div className="col-sm-8 d-flex gap-2">
            <button className="win-btn-primary mobile-view" onClick={handleNextClick}>{t('savebutton')}</button>
            {ucReqAuth === "Yes" && dynamicQuestions.length > 0 && (
              <button className="win-btn-secondary model-popup-btn me-3 mb-3 mb-sm-0" type="button" onClick={handleCancelDynamic}>Cancel</button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default RequestAuthorization;
